import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@material-ui/icons/Close';
import { Color } from '@sunrun/mui-theme';
import config from '../../config';
import { MODIFIED_MAX_BREAKPOINT } from '../general/constants/_breakpoints';
import { convertStringToUrlFormat as convertStr } from '../../utilities/convertStringToUrl';
import { useProspectId, useServiceContractId, useUserInfo } from '../../hooks/useUser';
import { DESKTOP_NAVBAR_WIDTH } from '../general/constants/_sizes';
import { SECONDARY_COLOR } from '../general/constants/_colors';
import { useDialpadChatFlag } from '../../hooks/flagsmith/useDialpadChatFlag';
import rollbar from '../../utilities/rollbar';

const Container = styled.div`
  position: fixed;
  z-index: 30;
  left: ${parseInt(DESKTOP_NAVBAR_WIDTH) + 50}px;
  min-width: 500px;
  border-radius: 5px 5px 0 0;
  box-shadow: 0px 3px 5px rgba(0, 0, 0, 0.2), 0px 6px 10px rgba(0, 0, 0, 0.14), 0px 1px 18px rgba(0, 0, 0, 0.12);
  bottom: ${({ display }) => (display ? '0' : '-500px')};
  transition: bottom 0.25s ease-in;

  // modified breakpoint for the support page because the design doesn't break well at the normal 1256 we have in _breakpoints.js
  // this follows the constant set in Support.js line 32 and 33
  @media only screen and (max-width: ${MODIFIED_MAX_BREAKPOINT}) {
    left: 30px;
    right: 30px;
    min-width: unset;
  }
`;

const Header = styled.div`
  position: relative;
  padding: 8px;
  color: ${Color.WHITE};
  background-color: ${SECONDARY_COLOR};
  border-radius: 5px 5px 0 0;
  text-align: center;

  svg {
    // close form icon
    position: absolute;
    top: 4px;
    right: 5px;
    cursor: pointer;
  }
`;

const ChatWindow = styled.div`
  background-color: ${Color.WHITE};
  height: 600px;
  width: 100%;

  iframe {
    border: none;
  }
`;

const ChatPopup = ({ isOpen, prospectId, serviceContractId, toggleChatPopup, userInfo }) => {
  const { t } = useTranslation();
  const isDialpadChatEnable = useDialpadChatFlag();

  const link = useMemo(() => {
    if (isDialpadChatEnable) {
      if (userInfo.firstName && userInfo.phoneNumber && userInfo.language && userInfo.email) {
        const name = convertStr(userInfo.firstName, '');
        const phone = convertStr(userInfo.phoneNumber, '');
        const language = convertStr(userInfo.language, '');
        const email = convertStr(userInfo.email, '');

        return `${config.DIALPAD_CHAT_LINK}&language=${language}&name=${name}&email=${email}&phone=${phone}`;
      }
      return config.DIALPAD_CHAT_LINK;
    }

    // custom query strings sent in incontact link for customer care agents
    // so they know info about who they are chatting with
    const p1 = serviceContractId;
    // the names could potentially have spaces in them, to
    const p2 = convertStr(userInfo.firstName, '');
    const p3 = convertStr(userInfo.lastName, '');
    const p4 = prospectId;
    const p5 = `${convertStr(userInfo.address, '_')}_${convertStr(userInfo.city, '_')}_${convertStr(userInfo.state, '_')}_${convertStr(userInfo.zip, '_')}`;
    const p6 = convertStr(userInfo.phoneNumber, '');
    const p7 = convertStr(userInfo.mobilePhoneNumber, '');
    return prospectId ? `${config.INCONTACT_CHAT_LINK}&p1=${p1}&p2=${p2}&p3=${p3}&p4=${p4}&p5=${p5}&p6=${p6}&p7=${p7}` : config.INCONTACT_CHAT_LINK;
  }, [userInfo, isDialpadChatEnable]);

  const handleReturnDataFromEmbeddedView = useCallback(({ nativeEvent }) => {
    let data;

    try {
      data = JSON.parse(nativeEvent.data);
    } catch (error) {
      rollbar.info('Error handling return data', {
        nativeEvent,
        error,
        eventData: `${nativeEvent.data}`,
        component: 'SupportChat',
      });
      return;
    }

    switch (data.type) {
      case 'chat-closed': {
        toggleChatPopup();
        break;
      }
      default: {
        // eslint-disable-next-line no-useless-return
        return;
      }
    }
    // eslint-disable-next-line consistent-return, no-useless-return
    return;
  }, []);

  useEffect(
    function handleReturnDataFromiFrame() {
      if (isDialpadChatEnable) {
        const handleDataFromIFrame = async e => {
          // eslint-disable-next-line no-constant-condition
          if (e.origin === 'https://us-east.dx.dialpad.com') {
            await handleReturnDataFromEmbeddedView({
              nativeEvent: {
                data: JSON.stringify(e.data),
              },
            });
          }
        };
        window.addEventListener('message', handleDataFromIFrame);
        return () => window.removeEventListener('message', handleDataFromIFrame);
      }
      // eslint-disable-next-line consistent-return, no-useless-return
      return;
    },
    [handleReturnDataFromEmbeddedView, isDialpadChatEnable],
  );

  return (
    <Container display={isOpen ? 1 : 0} data-testid="chat-popup">
      {isDialpadChatEnable ? null : (
        <Header>
          {t('Chat with a Care Agent')}
          <CloseIcon data-testid="close-chat-popup" onClick={toggleChatPopup} />
        </Header>
      )}
      {isOpen && (
        <ChatWindow>
          <iframe width="100%" height="100%" title="Contact a Care Agent" src={link} />
        </ChatWindow>
      )}
    </Container>
  );
};

ChatPopup.propTypes = {
  isOpen: PropTypes.bool,
  prospectId: PropTypes.string,
  serviceContractId: PropTypes.string,
  toggleChatPopup: PropTypes.func.isRequired,
  userInfo: PropTypes.shape({}).isRequired,
};

ChatPopup.defaultProps = {
  isOpen: false,
  prospectId: null,
  serviceContractId: null,
};

export const ChatPopupStandalone = ({ isOpen, toggleChatPopup }) => {
  const prospectId = useProspectId();
  const serviceContractId = useServiceContractId();
  const userInfo = useUserInfo();

  return <ChatPopup isOpen={isOpen} prospectId={prospectId} serviceContractId={serviceContractId} userInfo={userInfo} toggleChatPopup={toggleChatPopup} />;
};

ChatPopupStandalone.propTypes = {
  isOpen: PropTypes.bool,
  toggleChatPopup: PropTypes.func.isRequired,
};

ChatPopupStandalone.defaultProps = {
  isOpen: false,
};

export default ChatPopup;
